import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, MessageSquare, Phone, Mail, ChevronDown, MapPin, Clock, CheckCircle } from 'lucide-react';
import '../styles/Pages.css';
import '../styles/Support.css';
import { SupportHero } from '../components/PageHeroes';

const Support = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // Color removed from data — channel choice should come from the
  // copy (availability, what each one is for), not from an arbitrary
  // hue assigned by array position.
  const supportChannels = [
    {
      icon: MessageSquare,
      title: 'Live chat',
      description: 'Get instant answers from our support team in real-time. No waiting, no queues — just fast help with payroll, invoicing, accounting, or any part of the platform.',
      available: 'Available 24/7',
      action: 'Start chat',
      href: 'https://wa.me/447446301591'
    },
    {
      icon: Mail,
      title: 'Email support',
      description: 'Send us your query and we will respond with a detailed, personalised answer within one business hour.',
      available: 'Available 24/7',
      action: 'Send email',
      href: 'mailto:thomas@effectivestaff.co.uk'
    },
    {
      icon: Phone,
      title: 'Call or visit us',
      description: 'Speak directly with a specialist over the phone or visit our office for face-to-face support and guidance.',
      available: 'Mon – Fri, 9am – 6pm',
      action: 'Call now',
      href: 'tel:+447446301591',
      extra: 'Flat 11, 3rd Floor, 777 Old Kent Road, London, SE15 1NZ'
    }
  ];

  const faqs = [
    {
      question: 'How do I submit payroll in Effective Global Staff?',
      answer: 'Navigate to the Payroll section, enter employee details, and click "Process Payroll". The system automatically calculates taxes, National Insurance, and generates payslips — all filed to HMRC in real time.'
    },
    {
      question: 'What payment methods do you support?',
      answer: 'We currently support bank transfer for all plans, with card payments coming soon for UK businesses. You can choose monthly or annual billing to suit your business.'
    },
    {
      question: 'How do I export payroll data?',
      answer: 'All data can be exported as CSV or PDF directly from the Reports section. Your data is always yours and available to download at any time.'
    },
    {
      question: 'Is my data backed up automatically?',
      answer: 'Yes — we perform daily automated backups to geographically distributed servers with full encryption. All data is 100% recoverable.'
    },
    {
      question: 'Can I integrate with accounting software?',
      answer: 'Effective Global Staff already includes a full general ledger, journals, and VAT returns built in, so many businesses run their accounting entirely on the platform without a separate tool. For businesses who prefer to keep using Xero, Sage, QuickBooks, or another platform, get in touch and we\'ll help you find the right setup.'
    },
    {
      question: 'What if I need custom training for my team?',
      answer: 'Business-tier clients can request dedicated onboarding and training sessions covering any part of the platform. Reach out via email or phone and our team will put together a custom package.'
    },
    {
      question: 'How does HMRC RTI filing work?',
      answer: 'Every time you run payroll, Effective Global Staff automatically submits a Full Payment Submission (FPS) to HMRC in real time — no manual steps needed.'
    },
    {
      question: 'Can I invoice customers and manage suppliers from the same account?',
      answer: 'Yes — Invoicing, Accounts Payable, and Banking are all included alongside Payroll and HR, so you can send customer invoices, track supplier bills and expense claims, and reconcile your bank accounts from the same login.'
    },
    {
      question: 'Can I manage multiple companies from one account?',
      answer: 'Yes. Our multi-entity feature lets you switch between companies seamlessly with separate payroll runs, employee records, and reports for each.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } }
  };

  return (
    <div className="support-page">
      <SupportHero />

      {/* ===== SUPPORT CHANNELS ===== */}
      <section className="support-section">
        <div className="container">
          <motion.div
            className="support-section-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="support-eyebrow">Get help</span>
            <h2>How would you like to reach us?</h2>
            <p>Three ways to get expert support — choose whatever works best for you.</p>
          </motion.div>

          <motion.div
            className="support-channels-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {supportChannels.map((channel, idx) => {
              const Icon = channel.icon;
              return (
                <motion.div
                  key={idx}
                  className="support-channel-card"
                  variants={itemVariants}
                >
                  <div className="support-ch-icon">
                    <Icon size={26} />
                  </div>

                  <h3>{channel.title}</h3>
                  <p className="support-ch-desc">{channel.description}</p>

                  {channel.extra && (
                    <div className="support-ch-address">
                      <MapPin size={14} />
                      <span>{channel.extra}</span>
                    </div>
                  )}

                  <div className="support-ch-availability">
                    <Clock size={13} />
                    <span>{channel.available}</span>
                  </div>

                  <a href={channel.href} className="support-ch-btn">
                    {channel.action}
                  </a>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== FAQ ====================
          The strongest interactive element on this site so far — given
          the most deliberate visual treatment on the page instead of
          sitting in the same generic orb-background section as
          everything else. */}
      <section className="support-faq-section">
        <div className="container">
          <motion.div
            className="support-section-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="support-eyebrow">FAQs</span>
            <h2>Frequently asked questions</h2>
            <p>Quick answers to the questions we hear most often.</p>
          </motion.div>

          <div className="support-faq-list">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                className={`support-faq-item ${openFaq === idx ? 'faq-open' : ''}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.04, duration: 0.4 }}
              >
                <button
                  className="support-faq-question"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  aria-expanded={openFaq === idx}
                >
                  <span>{faq.question}</span>
                  <motion.div
                    className="faq-chevron"
                    animate={{ rotate: openFaq === idx ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <ChevronDown size={20} />
                  </motion.div>
                </button>

                <motion.div
                  className="support-faq-answer"
                  initial={false}
                  animate={openFaq === idx ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="faq-answer-inner">
                    <CheckCircle size={16} className="faq-answer-icon" />
                    <p>{faq.answer}</p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="support-cta-section">
        <div className="container">
          <motion.div
            className="support-cta-card"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="support-cta-icon">
              <HelpCircle size={24} />
            </div>
            <h2>Still can't find what you need?</h2>
            <p>Our support team is just a message away — we typically respond within one hour.</p>
            <a href="mailto:thomas@effectivestaff.co.uk" className="support-cta-btn">
              Contact support
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Support;